import { Box, BoxProps, Flex } from "@chakra-ui/react"
import { useEffect, useMemo } from "react"
import { useCoverPageContext } from "../CoverPageProvider"
import { parseVideoEmbedCode } from "../utils"
import TrailerDailymotion from "./TrailerDailymotion"
import TrailerLoom from "./TrailerLoom"
import TrailerVimeo from "./TrailerVimeo"
import TrailerYouTube from "./TrailerYouTube"

interface TrailerProps {
  trailerEmbedCode: string
  focused?: boolean
  backgroundMode?: boolean
  muted?: boolean
  autoplay?: boolean
  containerStyles?: BoxProps
  onParsePlatform?: (service: string | null) => void
}

const CoverPageTrailer = ({
  trailerEmbedCode,
  backgroundMode = false,
  muted: mutedProp = false,
  autoplay = true,
  containerStyles,
  onParsePlatform
}: TrailerProps) => {
  if (!trailerEmbedCode) return null
  const info = useMemo(
    () => parseVideoEmbedCode(trailerEmbedCode),
    [trailerEmbedCode]
  )

  // Choose platform component or fallback to raw embed
  let content: React.ReactNode
  if (!info) {
    content = (
      <Box
        position="relative"
        css={{
          aspectRatio: "16/9",
          "& iframe": { width: "100%", height: "100%", border: "none" }
        }}
        dangerouslySetInnerHTML={{ __html: trailerEmbedCode }}
      />
    )
  } else if (info.service === "youtube") {
    content = (
      <TrailerYouTube
        id={info.id}
        autoplay={autoplay}
        backgroundMode={!!backgroundMode}
        muted={!!mutedProp}
      />
    )
  } else if (info.service === "vimeo") {
    content = (
      <TrailerVimeo
        id={info.id}
        autoplay={autoplay}
        backgroundMode={!!backgroundMode}
        muted={!!mutedProp}
      />
    )
  } else if (info.service === "loom") {
    content = (
      <TrailerLoom
        id={info.id}
        autoplay={autoplay}
        backgroundMode={!!backgroundMode}
        muted={!!mutedProp}
      />
    )
  } else if (info.service === "dailymotion") {
    content = (
      <TrailerDailymotion
        id={info.id}
        autoplay={autoplay}
        backgroundMode={!!backgroundMode}
        muted={!!mutedProp}
      />
    )
  } else {
    content = (
      <Box
        position="relative"
        css={{
          aspectRatio: "16/9",
          "& iframe": { width: "100%", height: "100%", border: "none" }
        }}
        dangerouslySetInnerHTML={{ __html: trailerEmbedCode }}
      />
    )
  }

  // Notify parent about parsed platform (optional)
  useEffect(() => {
    onParsePlatform?.(info?.service ?? null)
  }, [info?.service, onParsePlatform])

  return (
    <Box
      className="trailer-wrapper"
      position="relative"
      css={{
        aspectRatio: "16/9",
        // Some embeds have a wrapper div that needs to be styled
        "& > div": {
          width: "100%",
          height: "100%"
        },
        "& iframe": {
          width: "100%",
          height: "100%",
          border: "none",
          background: "black",
          ...(backgroundMode && { pointerEvents: "none" })
        }
      }}
      bgColor="background"
      {...containerStyles}
    >
      {content}
      {backgroundMode && (
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          pointerEvents="none"
          zIndex={99}
          background="radial-gradient(ellipse 90% 90% at center, transparent 0%, rgba(0, 0, 0, 0.2) 50%, rgba(0, 0, 0, 0.5) 80%, rgba(0, 0, 0, 0.7) 100%)"
        />
      )}
    </Box>
  )
}

// Immersive focused trailer view (non-background mode)
export const CoverPageTrailerImmersive = () => {
  const { resource } = useCoverPageContext()
  const embed = resource?.trailer_embed_code
  if (!embed) return null

  return (
    <Flex className="CoverPageTrailerImmersive" w="100%" h="100%">
      <CoverPageTrailer
        trailerEmbedCode={embed}
        backgroundMode={false}
        muted={false}
        containerStyles={{
          width: "100%",
          height: "auto",
          maxWidth: "1000px",
          margin: "0 auto"
        }}
      />
    </Flex>
  )
}

export default CoverPageTrailer
